/**
 * Carga y validación del catálogo.
 *
 * Los JSON llegan sin tipar (TypeScript solo ve `string` donde el dominio espera
 * uniones literales), así que en vez de un `as` a ciegas se validan una vez al
 * arrancar. Un dato mal escrito revienta aquí, con un mensaje que dice qué carta
 * y qué campo — no tres capas más abajo con un NaN en el poder de una zona.
 *
 * En la Fase 2 estos mismos JSON se convierten en el seed de `cards_master`.
 */

import type {
  CardEffect,
  CardMaster,
  EffectType,
  LocationEffect,
  LocationMaster,
  Rarity,
} from '@/types/game';
import { DECK_SIZE, MAX_TURNS } from '@/types/game';
import rawCards from './cards.json';
import rawLocations from './locations.json';

const RARITIES: readonly Rarity[] = ['COMMON', 'RARE', 'EPIC', 'LEGENDARY'];
const EFFECT_TYPES: readonly EffectType[] = ['NONE', 'ON_REVEAL', 'PASSIVE'];

const CARD_EFFECT_KINDS: ReadonlySet<CardEffect['kind']> = new Set([
  'NONE',
  'DRAW_CARDS',
  'GAIN_ENERGY_NEXT_TURN',
  'BUFF_SELF_PER_ENEMY_HERE',
  'BUFF_SELF_IF_ALLIES_HERE',
  'DEBUFF_SELF_PER_ALLY_HERE',
  'DEBUFF_RANDOM_ENEMY_HERE',
  'BUFF_ALLIES_OTHER_ZONES',
  'AURA_BUFF_ALLIES_HERE',
]);

const LOCATION_EFFECT_KINDS: ReadonlySet<LocationEffect['kind']> = new Set([
  'NONE',
  'POWER_DELTA_ALL',
  'POWER_DELTA_BY_COST',
  'LOCK_ON_TURN',
]);

function fail(what: string, detail: string): never {
  throw new Error(`Catálogo inválido en ${what}: ${detail}`);
}

function parseCard(raw: unknown, index: number): CardMaster {
  const where = `cards.json[${index}]`;
  if (typeof raw !== 'object' || raw === null) fail(where, 'no es un objeto');
  const card = raw as Record<string, unknown>;

  const id = card['id'];
  if (typeof id !== 'string' || id.length === 0) fail(where, 'falta "id"');

  const effect = card['effect'] as { kind?: unknown } | undefined;
  if (!effect || !CARD_EFFECT_KINDS.has(effect.kind as CardEffect['kind'])) {
    fail(`${id}`, `efecto desconocido: ${JSON.stringify(effect?.kind)}`);
  }
  if (!RARITIES.includes(card['rarity'] as Rarity)) {
    fail(`${id}`, `rareza desconocida: ${String(card['rarity'])}`);
  }
  if (!EFFECT_TYPES.includes(card['effectType'] as EffectType)) {
    fail(`${id}`, `effectType desconocido: ${String(card['effectType'])}`);
  }
  if (typeof card['cost'] !== 'number' || typeof card['basePower'] !== 'number') {
    fail(`${id}`, '"cost" y "basePower" deben ser números');
  }
  // Una tirada de 0 dejaría la carta inacuñable para siempre, y un tope
  // fraccionario rompería el contador del servidor. Mejor no llegar a la base.
  const supply = card['maxSupply'];
  if (supply !== null && (typeof supply !== 'number' || !Number.isInteger(supply) || supply < 1)) {
    fail(`${id}`, `"maxSupply" debe ser null o un entero positivo, no ${JSON.stringify(supply)}`);
  }
  // Una carta con efecto declarado NONE pero con lógica real (o al revés) es el
  // fallo más fácil de colar al añadir cartas, y el más difícil de ver jugando.
  const isVanilla = (effect.kind as CardEffect['kind']) === 'NONE';
  if (isVanilla !== (card['effectType'] === 'NONE')) {
    fail(`${id}`, 'effectType y effect.kind se contradicen');
  }

  return card as unknown as CardMaster;
}

function parseLocation(raw: unknown, index: number): LocationMaster {
  const where = `locations.json[${index}]`;
  if (typeof raw !== 'object' || raw === null) fail(where, 'no es un objeto');
  const location = raw as Record<string, unknown>;

  const id = location['id'];
  if (typeof id !== 'string' || id.length === 0) fail(where, 'falta "id"');

  const effect = location['effect'] as { kind?: unknown; turn?: unknown } | undefined;
  if (!effect || !LOCATION_EFFECT_KINDS.has(effect.kind as LocationEffect['kind'])) {
    fail(`${id}`, `efecto desconocido: ${JSON.stringify(effect?.kind)}`);
  }

  // "LAST" se resuelve aquí y no en el motor: el JSON expresa la intención de
  // diseño ("el último turno", que es cuando bloquear una zona duele de verdad)
  // y el motor sigue viendo un número. Escribir un 6 a mano funcionaría hasta el
  // día que la partida pase a durar 10 turnos y la ubicación dejara de hacer
  // nada sin que nadie se enterase.
  if (effect.kind === 'LOCK_ON_TURN' && effect.turn === 'LAST') {
    effect.turn = MAX_TURNS;
  }
  if (effect.kind === 'LOCK_ON_TURN' && typeof effect.turn !== 'number') {
    fail(`${id}`, `LOCK_ON_TURN necesita un número o "LAST", no ${JSON.stringify(effect.turn)}`);
  }

  return location as unknown as LocationMaster;
}

/** Las 12 cartas base, ya validadas. */
export const CARD_POOL: readonly CardMaster[] = (rawCards as unknown[]).map(parseCard);

/** Pool de ubicaciones del que se sortean las 3 zonas de cada partida. */
export const LOCATION_POOL: readonly LocationMaster[] = (rawLocations as unknown[]).map(
  parseLocation,
);

const CARD_BY_ID = new Map(CARD_POOL.map((card) => [card.id, card]));

/**
 * Definición de una carta por su id maestro.
 * Lanza si no existe: un id inválido en un mazo es un bug, no un caso a tolerar.
 */
export function getCardMaster(masterId: string): CardMaster {
  const card = CARD_BY_ID.get(masterId);
  if (!card) throw new Error(`Carta desconocida: ${masterId}`);
  return card;
}

/**
 * Mazo inicial de pruebas, del tamaño que exijan las reglas activas.
 *
 * Recorre el pool en ciclo, así que si el mazo es mayor que el catálogo se
 * repiten cartas empezando por las más baratas — que es justo la mitad de la
 * curva que conviene duplicar cuando la partida se alarga y el tablero se llena.
 *
 * Cuando el catálogo crezca en la Fase 3 (sobres y colección), esto lo sustituye
 * el mazo real del jugador y la repetición desaparece sola.
 */
export const STARTER_DECK_IDS: readonly string[] = Array.from({ length: DECK_SIZE }, (_, index) => {
  const card = CARD_POOL[index % CARD_POOL.length];
  if (!card) throw new Error('El catálogo de cartas está vacío');
  return card.id;
});
